import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { AppError, indexBy, SpinnerService } from '@hglobal/common';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, switchMap } from 'rxjs';

import { Account, Currency, parseRoleFromApi, Role, WalletType } from '@pp/core/entities';
import { setErrorStatusFor, setPendingStatusFor, setSuccessStatusFor, withRequestHandler, withRequestStatus } from '@pp/core/store';

import { DICTIONARIES_API } from './api';
import { DictionariesState } from './models';

const initialState: DictionariesState = {
  cardSkins: [],
  countries: [],
  roles: [],
  currency: null,
  currencies: null,
  walletSkins: null,
  user: null,
};

export const DictionariesStore = signalStore(
  withState(initialState),
  withRequestStatus(['getRoles', 'getCurrencies', 'getCardsSkins', 'getWalletSkins', 'getCountries']),
  withRequestHandler(),
  withProps(store => ({
    store,
    spinnerService: inject(SpinnerService),
    dictionariesApi: inject(DICTIONARIES_API),
  })),
  withComputed(store => ({
    selectRolesWithoutSuperAdmin: computed(() => {
      const roles = store.roles();
      const user = store.user();
      if (!user) return roles;
      const myRole = parseRoleFromApi(user.role);
      return myRole === Role.SuperAdmin ? roles : roles.filter(r => parseRoleFromApi(r.id) !== Role.SuperAdmin);
    }),
    selectFiatCurrencies: computed(() => store.currencies()?.fiat || []),
    selectCryptoCurrencies: computed(() => store.currencies()?.crypto || []),
    selectUserRole: computed(() => store.user()?.role && parseRoleFromApi(store.user().role)),
    selectRolesForFilters: computed(() => {
      const roles = store.roles();
      const user = store.user();
      if (!user) return [];
      const myRole = parseRoleFromApi(user.role);
      if (myRole === Role.SuperAdmin) {
        return roles;
      } else if (myRole === Role.Supervisor) {
        return [...roles, { id: Role.Admin, name: 'dictionary.role.admin' }];
      } else {
        return roles.filter(r => parseRoleFromApi(r.id) !== Role.SuperAdmin);
      }
    }),
  })),
  withComputed(store => ({
    selectAllCurrencies: computed(() => [...store.selectFiatCurrencies(), ...store.selectCryptoCurrencies()]),
    selectIsReadyForGetWallets: computed(() => {
      return (store.isGetCurrenciesSuccess() || store.getCurrenciesError()) && store.isGetWalletSkinsSuccess();
    }),
  })),
  withMethods(store => ({
    selectCurrencyByEntity: (currency: string, entity: string) => {
      const currencies = store.selectAllCurrencies();
      const currenciesIndex = indexBy(currencies, entity);
      return currenciesIndex[currency] || null;
    },
    clearRole: () => patchState(store, { roles: [] }),
    selectWalletSkinsByType: (type: WalletType) => computed(() => store.walletSkins()?.[type]),
    changeCurrency: (currency: Currency) => patchState(store, { currency }),
    setUser: (user: Account) => patchState(store, { user }),
  })),
  withMethods(store => ({
    selectCurrencyByCode: (currency: string) => computed(() => store.selectCurrencyByEntity(currency, 'code')),
    selectCurrencyByName: (currency: string) => computed(() => store.selectCurrencyByEntity(currency, 'name')),
  })),
  withMethods(({ store, dictionariesApi }) => ({
    getRoles: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getRoles'));
        return (store.roles().length ? of(store.roles()) : dictionariesApi.getRoles()).pipe(
          tapResponse({
            next: roles => {
              patchState(store, { roles }, setSuccessStatusFor('getRoles'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getRoles', error)),
          }),
        );
      }),
    ),
    getCurrencies: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getCurrencies'));
        return (store.currencies() ? of(store.currencies()) : dictionariesApi.getCurrencies()).pipe(
          tapResponse({
            next: currencies => {
              patchState(store, { currencies }, setSuccessStatusFor('getCurrencies'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getCurrencies', error)),
          }),
        );
      }),
    ),
    getCardsSkins: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getCardsSkins'));
        return (store.cardSkins().length ? of(store.cardSkins()) : dictionariesApi.getCardsSkins()).pipe(
          tapResponse({
            next: cardSkins => {
              patchState(store, { cardSkins }, setSuccessStatusFor('getCardsSkins'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getCardsSkins', error)),
          }),
        );
      }),
    ),
    getWalletSkins: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getWalletSkins'));
        return (store.walletSkins() ? of(store.walletSkins()) : dictionariesApi.getWalletSkins()).pipe(
          tapResponse({
            next: walletSkins => {
              patchState(store, { walletSkins }, setSuccessStatusFor('getWalletSkins'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getWalletSkins', error)),
          }),
        );
      }),
    ),
    getCountries: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getCountries'));
        return (store.countries().length ? of(store.countries()) : dictionariesApi.getCountries()).pipe(
          tapResponse({
            next: countries => {
              patchState(store, { countries }, setSuccessStatusFor('getCountries'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getCountries', error)),
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      store.spinnerService.observe(toObservable(store.isLoading));
    },
  }),
);
